// Parent view of a child's grades per course (moyenne per matière + overall).
// Shows 0/20 until grades are entered; live current-semester default like the
// other parent pages.
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Award, ChevronDown, ChevronRight, Loader2, TrendingUp, User } from 'lucide-react';
import { ChildSelector } from '../components/ChildSelector';
import { fetchChildClasses, fetchChildModuleGrades } from '../services/parentService';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import { useAuthStore } from '@/features/auth/stores/authStore';

interface ChildClassInfo {
  moduleElementId: string;
  moduleElementName: string;
  moduleCode: string;
  professorName: string;
  semester: number;
  gradeAverage: number | null;
  hasRattrapage?: boolean;
}

const getCurrentSemester = (year: AcademicYear): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const s1Start = year.semester1StartDate ? new Date(year.semester1StartDate) : new Date(year.startDate);
  const s1End = year.semester1EndDate ? new Date(year.semester1EndDate) : null;
  const s2Start = year.semester2StartDate ? new Date(year.semester2StartDate) : null;
  const s2End = year.semester2EndDate ? new Date(year.semester2EndDate) : new Date(year.endDate);
  if (s1End && today >= s1Start && today <= s1End) return 1;
  if (s2Start && s2End && today >= s2Start && today <= s2End) return 2;
  if (today > s2End) return 2;
  if (s1End && today > s1End) return 2;
  return 1;
};

const gradeColor = (grade: number | null): string => {
  if (grade === null || grade === 0) return 'text-gray-600 bg-gray-100';
  if (grade >= 14) return 'text-green-600 bg-green-50';
  if (grade >= 10) return 'text-blue-600 bg-blue-50';
  return 'text-red-600 bg-red-50';
};

interface GradeComponentScore {
  code: string;
  name: string;
  percentage: number;
  maxScore: number;
  score: number | null;
  absenceStatus?: string;
}

// Expanded detail: per-component scores of the child for one course
const CourseGradesDetail: React.FC<{ childId: string; moduleElementId: string }> = ({ childId, moduleElementId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['parent-child-module-grades', childId, moduleElementId],
    queryFn: () => fetchChildModuleGrades(childId, moduleElementId),
  });

  const breakdown = data as { components?: GradeComponentScore[]; averageGrade?: number | null; status?: string } | undefined;
  const components = breakdown?.components || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4 bg-gray-50">
        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
      </div>
    );
  }
  if (components.length === 0) {
    return <p className="px-6 py-3 text-sm text-gray-500 bg-gray-50">Aucune composante de note configurée</p>;
  }
  return (
    <div className="bg-gray-50 px-6 py-3">
      {components.map((comp) => (
        <div key={comp.code} className="flex items-center justify-between text-sm py-1.5">
          <span className="text-gray-600">
            {comp.name} <span className="text-xs text-gray-400">({comp.percentage}%)</span>
          </span>
          <span className={`px-2.5 py-0.5 text-sm font-semibold rounded-full ${gradeColor(comp.score ?? 0)}`}>
            {comp.score !== null && comp.score !== undefined ? `${comp.score}/${comp.maxScore}` : `—/${comp.maxScore}`}
          </span>
        </div>
      ))}
      {breakdown?.status && breakdown.status !== 'INCOMPLETE' && (
        <p className="text-xs text-gray-500 pt-1">Statut : {breakdown.status}</p>
      )}
    </div>
  );
};

const ParentGradesPage: React.FC = () => {
  const { tokens } = useAuthStore();
  const [childId, setChildId] = useState<string | null>(null);
  const [currentYear, setCurrentYear] = useState<AcademicYear | null>(null);
  const [selectedSemester, setSelectedSemester] = useState<number | 'all'>('all');
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!tokens?.accessToken) return;
      try {
        const years = await fetchAcademicYears(tokens.accessToken);
        const current = years.find((y: AcademicYear) => y.isCurrent);
        if (current) {
          setCurrentYear(current);
          setSelectedSemester(getCurrentSemester(current));
        }
      } catch {
        // keep defaults
      }
    };
    load();
  }, [tokens?.accessToken]);

  const { data, isLoading } = useQuery({
    queryKey: ['parent-child-grades', childId, currentYear?.id],
    queryFn: () => fetchChildClasses(childId as string, currentYear?.id) as Promise<ChildClassInfo[]>,
    enabled: !!childId,
  });

  const classes = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const semesterLabels = useMemo(() => {
    const abs = [...new Set(classes.map((c) => c.semester).filter(Boolean))].sort((a, b) => a - b);
    const odd = abs.find((s) => s % 2 === 1);
    const even = abs.find((s) => s % 2 === 0);
    return { 1: odd ? `Semestre ${odd}` : 'Semestre 1', 2: even ? `Semestre ${even}` : 'Semestre 2' } as Record<number, string>;
  }, [classes]);

  const filtered = useMemo(
    () =>
      classes.filter((c) => {
        if (selectedSemester === 'all' || !c.semester) return true;
        return ((c.semester - 1) % 2) + 1 === selectedSemester;
      }),
    [classes, selectedSemester],
  );

  const overall = useMemo(() => {
    const graded = filtered.filter((c) => c.gradeAverage !== null && c.gradeAverage !== undefined);
    if (!graded.length) return 0;
    return Math.round((graded.reduce((s, c) => s + (c.gradeAverage || 0), 0) / graded.length) * 10) / 10;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 pt-6">
        <ChildSelector onChildChange={setChildId} />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notes</h1>
            <p className="mt-1 text-sm text-gray-500">
              Moyennes par cours{currentYear ? ` — ${currentYear.name}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg ${gradeColor(overall)}`}>
              <TrendingUp className="h-4 w-4" />
              Moyenne générale : {overall.toFixed(1)}/20
            </span>
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              {([1, 2] as const).map((sem) => (
                <button
                  key={sem}
                  onClick={() => setSelectedSemester(sem)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    selectedSemester === sem ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {semesterLabels[sem]}
                </button>
              ))}
              <button
                onClick={() => setSelectedSemester('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectedSemester === 'all' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Tous
              </button>
            </div>
          </div>
        </div>

        {!childId ? (
          <p className="text-center text-gray-500 py-12">Sélectionnez un enfant pour voir ses notes</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Award className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p className="text-gray-500">Aucun cours pour cette période</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {filtered
              .slice()
              .sort((a, b) => (a.semester || 0) - (b.semester || 0) || a.moduleElementName.localeCompare(b.moduleElementName, 'fr'))
              .map((cls) => (
                <div key={cls.moduleElementId}>
                  <div
                    onClick={() => setExpandedCourse(expandedCourse === cls.moduleElementId ? null : cls.moduleElementId)}
                    className="px-6 py-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {expandedCourse === cls.moduleElementId ? (
                          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}
                        <p className="font-medium text-gray-900 truncate">{cls.moduleElementName}</p>
                        <span className="inline-flex items-center px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded-full flex-shrink-0">
                          S{cls.semester}
                        </span>
                      </div>
                      <p className="flex items-center gap-1 mt-1 ml-6 text-sm text-gray-500">
                        <User className="h-3.5 w-3.5" />
                        {cls.professorName}
                      </p>
                    </div>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      {cls.hasRattrapage && (
                        <span className="inline-flex items-center px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                          Rattrapage
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg ${gradeColor(cls.gradeAverage ?? 0)}`}>
                        <Award className="h-4 w-4" />
                        {cls.gradeAverage !== null && cls.gradeAverage !== undefined ? `${cls.gradeAverage.toFixed(1)}/20` : '—/20'}
                      </span>
                    </span>
                  </div>
                  {expandedCourse === cls.moduleElementId && childId && (
                    <CourseGradesDetail childId={childId} moduleElementId={cls.moduleElementId} />
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentGradesPage;
